import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { type CustomerDetail, type CustomerRow, formatDate, formatPhone, telHref } from '@reikraft/shared';
import { type Column, ErrorBox, Field, Icon, Kpi, Kpis, Loader, Modal, Panel, SearchBox, Table, useToast } from '@reikraft/ui';
import { api, qs } from '../api';
import { FormMessage, OrderCards, PageHead, useFormErrors } from '../components/common';
import { PaymentModal } from '../components/PaymentModal';
import { formatMoney } from '../format';
import { useDebounced } from '../hooks';

interface CustomerForm {
  name: string;
  phone: string;
  email: string;
  avitoUrl: string;
  notes: string;
}

const emptyForm: CustomerForm = { name: '', phone: '', email: '', avitoUrl: '', notes: '' };

function CustomerModal({ open, onClose, initial, id }: { open: boolean; onClose: () => void; initial?: CustomerRow; id?: string }) {
  const [f, setF] = useState<CustomerForm>(emptyForm);
  const errs = useFormErrors();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  useEffect(() => {
    if (open) {
      setF(
        initial
          ? {
              name: initial.name,
              phone: initial.phone ?? '',
              email: initial.email ?? '',
              avitoUrl: initial.avitoUrl ?? '',
              notes: initial.notes ?? '',
            }
          : emptyForm,
      );
      errs.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const save = useMutation({
    mutationFn: () => (id ? api.put<CustomerDetail>(`/admin/customers/${id}`, f) : api.post<CustomerDetail>('/admin/customers', f)),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      qc.setQueryData(['customer', c.id], c);
      toast('Клиент сохранён');
      onClose();
      if (!id) void navigate(`/clients/${c.id}`);
    },
    onError: (e) => errs.set(e),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const bind = (k: keyof CustomerForm) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={id ? 'Клиент' : 'Новый клиент'}
      footer={
        <button type="submit" form="customer-form" className="rk-btn rk-btn--primary" disabled={save.isPending}>
          Сохранить
        </button>
      }
    >
      <form id="customer-form" onSubmit={submit} className="rk-stack">
        <FormMessage message={errs.message} />
        <Field label="Имя" error={errs.errors.name}>
          <input className="rk-input" {...bind('name')} autoFocus />
        </Field>
        <div className="rk-grid-form">
          <Field label="Телефон" error={errs.errors.phone}>
            <input className="rk-input rk-input--mono" type="tel" {...bind('phone')} />
          </Field>
          <Field label="E-mail" error={errs.errors.email}>
            <input className="rk-input" type="email" {...bind('email')} />
          </Field>
        </div>
        <Field label="Профиль на Авито">
          <input className="rk-input" {...bind('avitoUrl')} />
        </Field>
        <Field label="Заметки">
          <textarea className="rk-textarea" rows={3} {...bind('notes')} />
        </Field>
      </form>
    </Modal>
  );
}

export function ClientsPage() {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['customers', dq], queryFn: () => api.get<CustomerRow[]>(`/admin/customers${qs({ q: dq })}`) });
  const columns: Column<CustomerRow>[] = [
    { key: 'name', title: 'Клиент', width: 'minmax(180px, 1.4fr)', render: (c) => <b style={{ fontWeight: 600 }}>{c.name}</b> },
    { key: 'phone', title: 'Телефон', width: '170px', render: (c) => <span className="rk-mono">{formatPhone(c.phone)}</span> },
    {
      key: 'avito',
      title: 'Авито',
      width: '70px',
      render: (c) => (c.avitoUrl ? <span className="rk-mono">есть</span> : <span className="rk-muted">—</span>),
    },
    { key: 'orders', title: 'Заказов', width: '80px', render: (c) => <span className="rk-mono">{c.ordersCount}</span> },
    {
      key: 'last',
      title: 'Последний',
      width: '110px',
      render: (c) => <span className="rk-mono rk-text-2">{c.lastOrderAt ? formatDate(c.lastOrderAt) : '—'}</span>,
    },
    {
      key: 'debt',
      title: 'Долг',
      width: '110px',
      align: 'right',
      render: (c) => (
        <b className="rk-mono" style={{ color: c.debtKop > 0 ? 'var(--danger-text)' : 'var(--muted)' }}>
          {c.debtKop > 0 ? formatMoney(c.debtKop) : '—'}
        </b>
      ),
    },
  ];
  return (
    <>
      <PageHead
        title="Клиенты"
        sub="Частные лица. Автосервисы и автопарки — в разделе «Организации»"
        actions={
          <button type="button" className="rk-btn rk-btn--primary" onClick={() => setOpen(true)}>
            <Icon name="plus" size={16} /> Новый клиент
          </button>
        }
      >
        <SearchBox value={q} onChange={setQ} placeholder="Имя, телефон, e-mail" label="Поиск клиента" />
      </PageHead>
      {list.isLoading ? (
        <Loader />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <Table
          label="Клиенты"
          columns={columns}
          rows={list.data!}
          rowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/clients/${c.id}`)}
          minWidth={760}
          empty="Клиентов не найдено"
        />
      )}
      <CustomerModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function CustomerPage() {
  const { id = '' } = useParams();
  const [edit, setEdit] = useState(false);
  const [pay, setPay] = useState(false);
  const q = useQuery({ queryKey: ['customer', id], queryFn: () => api.get<CustomerDetail>(`/admin/customers/${id}`) });
  if (q.isLoading) return <Loader />;
  if (q.isError || !q.data) return <ErrorBox error={q.error} />;
  const c = q.data;
  return (
    <>
      <PageHead
        crumbs={[{ label: 'Клиенты', to: '/clients' }, { label: c.name }]}
        title={c.name}
        sub={[formatPhone(c.phone), c.email].filter((x) => x && x !== '—').join(' · ')}
        actions={
          <>
            {c.phone && (
              <a className="rk-btn" href={telHref(c.phone)}>
                Позвонить
              </a>
            )}
            {c.avitoUrl && (
              <a className="rk-btn" href={c.avitoUrl} target="_blank" rel="noreferrer">
                Авито
              </a>
            )}
            <button type="button" className="rk-btn" onClick={() => setEdit(true)}>
              Изменить
            </button>
            <Link to={`/orders/new?customerId=${c.id}&name=${encodeURIComponent(c.name)}`} className="rk-btn rk-btn--primary">
              Новый заказ
            </Link>
          </>
        }
      />
      <Kpis>
        <Kpi label="Заказов" value={c.ordersCount} />
        <Kpi label="Долг" value={formatMoney(c.debtKop)} tone={c.debtKop > 0 ? 'danger' : undefined}>
          {c.debtKop > 0 && (
            <button type="button" className="rk-btn rk-btn--sm" style={{ marginTop: 8 }} onClick={() => setPay(true)}>
              Принять оплату
            </button>
          )}
        </Kpi>
        <Kpi label="Клиент с" value={formatDate(c.createdAt)} />
      </Kpis>
      {c.notes && <Panel title="Заметки">{c.notes}</Panel>}
      <Panel title="Заказы" bodyless>
        <OrderCards items={c.orders} />
      </Panel>
      <CustomerModal open={edit} onClose={() => setEdit(false)} initial={c} id={c.id} />
      <PaymentModal
        open={pay}
        onClose={() => setPay(false)}
        party={{ kind: 'customer', id: c.id, name: c.name }}
        defaultAmountKop={c.debtKop}
      />
    </>
  );
}
